import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AddableReachability } from './notifications-forwardings.model';
import { EMAIL_FORMAT_OPTIONS } from '../reachabilities/reachabilities.model';
import { typeBadgeVariant } from '../../shared/utils/reachability-type-badge';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmSwitchComponent,
  HlmBadgeComponent,
  HlmTooltipDirective,
  HLM_FORM_FIELD,
  HLM_TABS,
  HlmSelectOption,
} from '../../ui';

export interface AddReachabilityDialogData {
  addableList: AddableReachability[];
  sectionLabel: string;
  /** When false, the "Create new" tab is hidden. Default: true. */
  allowCreate?: boolean;
  /**
   * Reachability types the user may CREATE in this section (mirrors the old
   * CMT restrictions). Omitted → the full default set. A single entry pins the
   * type (no selector shown).
   */
  allowedTypes?: string[];
}

export type AddReachabilityDialogResult =
  | { mode: 'existing'; reachabilityId: number }
  | {
      mode: 'create';
      name: string;
      destination: string;
      type: string;
      publiclyAvailable: boolean;
      /** Only populated when type === 'EMAIL'. */
      callNotificationEmailFormat: string | null;
    };

const REACHABILITY_TYPES = ['EMAIL', 'FAX', 'SMS', 'TELEFON', 'HANDY'];
const DEFAULT_EMAIL_FORMAT = 'HTML';

@Component({
  selector: 'cmt-add-reachability-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    ...HLM_FORM_FIELD,
    ...HLM_TABS,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective,
    HlmSelectComponent, HlmSwitchComponent, HlmBadgeComponent,
    HlmTooltipDirective,
  ],
  template: `
    <hlm-dialog-surface size="lg">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucidePlus" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Add {{ data.sectionLabel }}</h2>
          <p hlmDialogDescription>Select available reachability or create a new one</p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <!-- overflow-y-visible: the reachability list below scrolls on its own
           (max-h-[50vh]); without this the body would scroll too, producing a
           redundant outer scrollbar (EB-3856). -->
      <section hlmDialogBody class="overflow-y-visible">
        <hlm-tabs [value]="activeTab === 0 ? 'existing' : 'create'" (valueChange)="activeTab = $any($event) === 'create' ? 1 : 0">
          <hlm-tab value="existing" label="Select existing">
            <div class="flex flex-col gap-3 pt-4">
              @if (data.addableList.length > 0) {
                <!-- Filter input -->
                <div class="relative">
                  <hlm-icon name="lucideSearch" size="0.9rem" class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input hlmInput class="pl-9 pr-9" placeholder="Filter by name, destination or type..."
                    [value]="filterText()"
                    (input)="filterText.set($any($event.target).value)" />
                  @if (filterText()) {
                    <button hlmBtn variant="ghost" size="icon" class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                      (click)="filterText.set('')" type="button" tabindex="-1">
                      <hlm-icon name="lucideX" size="0.8rem" />
                    </button>
                  }
                </div>

                <p class="text-sm text-muted-foreground">
                  {{ filteredList().length }} of {{ data.addableList.length }}
                  reachabilit{{ data.addableList.length === 1 ? 'y' : 'ies' }}
                </p>

                @if (filteredList().length > 0) {
                  <div class="max-h-[50vh] overflow-y-auto rounded-xl border border-border bg-card">
                    @for (r of filteredList(); track r.reachabilityId) {
                      <div class="flex cursor-pointer items-center gap-2 border-b border-border/50 px-3 py-2 transition-colors last:border-b-0 hover:bg-muted/30"
                        [class]="selectedId.value === r.reachabilityId ? 'bg-primary/10' : ''"
                        (click)="selectedId.setValue(r.reachabilityId)">
                        <div class="flex-shrink-0">
                          @if (selectedId.value === r.reachabilityId) {
                            <hlm-icon name="lucideCircleCheck" size="1rem" class="text-primary" />
                          } @else {
                            <hlm-icon name="lucideCircle" size="1rem" class="text-border" />
                          }
                        </div>
                        <div class="flex min-w-0 flex-1 items-center gap-2">
                          <hlm-badge variant="secondary" class="flex-shrink-0">{{ r.type }}</hlm-badge>
                          <span class="max-w-[200px] truncate text-sm font-medium text-foreground">{{ r.name }}</span>
                          <span class="min-w-0 flex-1 truncate text-sm text-muted-foreground" [hlmTooltip]="r.destination">{{ r.destination }}</span>
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="flex items-start gap-2 rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
                    <hlm-icon name="lucideSearchX" size="1rem" class="mt-0.5 flex-shrink-0" />
                    <span>No reachabilities match "{{ filterText() }}".</span>
                  </div>
                }
              } @else {
                <div class="flex items-start gap-2 rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
                  <hlm-icon name="lucideInfo" size="1rem" class="mt-0.5 flex-shrink-0" />
                  @if (allowCreate()) {
                    <span>No reachabilities available. Switch to "Create new" to add one.</span>
                  } @else {
                    <span>No reachabilities available to add.</span>
                  }
                </div>
              }
            </div>
          </hlm-tab>

          @if (allowCreate()) {
            <hlm-tab value="create" label="Create new">
              <div class="flex flex-col gap-4 pt-4">
                <p class="text-sm text-muted-foreground">Create a new reachability and add it directly.</p>

                <hlm-form-field>
                  <label hlmLabel>Type</label>
                  @if (typeFixed) {
                    <hlm-badge [variant]="typeBadgeVariant(allowedTypes[0])" class="w-fit">{{ allowedTypes[0] }}</hlm-badge>
                  } @else {
                    <hlm-select
                      [options]="typeSelectOptions"
                      [value]="createForm.controls.type.value"
                      (valueChange)="createForm.controls.type.setValue($any($event) || allowedTypes[0])"
                    />
                  }
                </hlm-form-field>

                <hlm-form-field>
                  <label hlmLabel>Name</label>
                  <input hlmInput [formControl]="createForm.controls.name" placeholder="Display name shown in lists" />
                </hlm-form-field>

                <hlm-form-field>
                  <label hlmLabel>Destination</label>
                  <input hlmInput [formControl]="createForm.controls.destination"
                    [placeholder]="destinationPlaceholder()" />
                  <p hlmFieldHint>{{ destinationHint() }}</p>
                </hlm-form-field>

                @if (isEmailType()) {
                  <hlm-form-field>
                    <label hlmLabel>Email format</label>
                    <hlm-select
                      [options]="emailFormatSelectOptions"
                      [value]="createForm.controls.callNotificationEmailFormat.value"
                      (valueChange)="createForm.controls.callNotificationEmailFormat.setValue($any($event) || 'HTML')"
                    />
                    <p hlmFieldHint>Controls how call notifications are rendered in the email body.</p>
                  </hlm-form-field>
                }

                <div class="flex items-center gap-3 py-1">
                  <hlm-switch
                    [checked]="createForm.controls.publiclyAvailable.value"
                    (checkedChange)="createForm.controls.publiclyAvailable.setValue($any($event))"
                    label="Publicly available"
                  />
                  <span class="text-sm text-muted-foreground">Agent may disclose this reachability.</span>
                </div>
              </div>
            </hlm-tab>
          }
        </hlm-tabs>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()">Cancel</button>
        @if (activeTab === 0) {
          <button hlmBtn (click)="addExisting()" [disabled]="!selectedId.value">
            <hlm-icon name="lucidePlus" size="0.9rem" />
            Add Selected
          </button>
        } @else {
          <button hlmBtn (click)="createAndAdd()" [disabled]="createForm.invalid">
            <hlm-icon name="lucidePlus" size="0.9rem" />
            Create &amp; Add
          </button>
        }
      </footer>
    </hlm-dialog-surface>
  `,
})
export class AddReachabilityDialogComponent {
  readonly dialogRef = inject(MatDialogRef<AddReachabilityDialogComponent>);
  readonly data: AddReachabilityDialogData = inject(MAT_DIALOG_DATA);
  /** Types the user may create here — section-restricted (old CMT parity), else the default set. */
  readonly allowedTypes: string[] = this.data.allowedTypes?.length ? this.data.allowedTypes : REACHABILITY_TYPES;
  /** A single allowed type pins the selection — no dropdown shown. */
  readonly typeFixed = this.allowedTypes.length === 1;
  readonly typeBadgeVariant = typeBadgeVariant;
  readonly emailFormatOptions = EMAIL_FORMAT_OPTIONS;
  readonly typeSelectOptions: HlmSelectOption<string>[] = this.allowedTypes.map(t => ({ value: t, label: t }));
  readonly emailFormatSelectOptions: HlmSelectOption<string>[] = EMAIL_FORMAT_OPTIONS.map(f => ({ value: f.value, label: f.label }));

  activeTab = 0;
  readonly selectedId = new FormControl<number | null>(null);

  // Create form — mirrors the old CMT fields:
  // - name, destination, type (all required)
  // - publiclyAvailable toggle (default: false)
  // - callNotificationEmailFormat (default: HTML, only used when type === 'EMAIL')
  readonly createForm = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    destination: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    type: new FormControl(this.allowedTypes[0], { nonNullable: true, validators: [Validators.required] }),
    publiclyAvailable: new FormControl(false, { nonNullable: true }),
    callNotificationEmailFormat: new FormControl<string>(DEFAULT_EMAIL_FORMAT, { nonNullable: true }),
  });

  // Reactive type signal so the template can toggle type-specific UI
  // (destination placeholder, email-format row) without manual subscriptions.
  private readonly typeValue = toSignal(this.createForm.controls.type.valueChanges, {
    initialValue: this.createForm.controls.type.value,
  });

  readonly isEmailType = computed(() => this.typeValue() === 'EMAIL');

  /**
   * Placeholder shown in the Destination field — hints at the expected format
   * per type, matching old CMT's type-specific forms (email vs phone).
   */
  readonly destinationPlaceholder = computed(() => {
    switch (this.typeValue()) {
      case 'EMAIL': return 'name@example.com';
      case 'SMS':
      case 'FAX':
      case 'TELEFON':
      case 'HANDY':
      case 'BUSY':
      case 'DIREKT': return '+49 30 1234567';
      default: return '';
    }
  });

  readonly destinationHint = computed(() =>
    this.isEmailType()
      ? 'A valid email address.'
      : 'International phone format recommended, e.g. +49 30 1234567.',
  );

  // Filter state — narrows the visible reachability list as the user types
  readonly filterText = signal('');

  readonly filteredList = computed<ReadonlyArray<AddableReachability>>(() => {
    const q = this.filterText().toLowerCase().trim();
    if (!q) return this.data.addableList;
    return this.data.addableList.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.destination.toLowerCase().includes(q) ||
      r.type.toLowerCase().includes(q),
    );
  });

  /** Whether the "Create new" tab is allowed (defaults to true). */
  readonly allowCreate = computed(() => this.data.allowCreate !== false);

  addExisting(): void {
    if (!this.selectedId.value) return;
    this.dialogRef.close({ mode: 'existing', reachabilityId: this.selectedId.value } as AddReachabilityDialogResult);
  }

  createAndAdd(): void {
    if (this.createForm.invalid) return;
    const v = this.createForm.getRawValue();
    // callNotificationEmailFormat is only meaningful for EMAIL — explicitly
    // null it for other types so downstream code doesn't have to guard.
    const result: AddReachabilityDialogResult = {
      mode: 'create',
      name: v.name,
      destination: v.destination,
      type: v.type,
      publiclyAvailable: v.publiclyAvailable,
      callNotificationEmailFormat: v.type === 'EMAIL' ? v.callNotificationEmailFormat : null,
    };
    this.dialogRef.close(result);
  }
}
